import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { createSvgIcon, IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import { IoWarningOutline } from 'react-icons/io5';
import { fontSize, palette } from '../../..';
import MenuIcon from '@mui/icons-material/Menu';
import FunctionsIcon from '@mui/icons-material/Functions';
import { NotificationDialog } from '../../CommonFunctions/DialogComponents';
import { AccessTime, CalendarToday } from '@mui/icons-material';
import { Area } from '../../CommonFunctions/aliases';
import LockIcon from '@mui/icons-material/Lock';
import DoneIcon from '@mui/icons-material/Done';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../redux';
import { isFlowFilledProperly } from '../utils';
import FlowWindowFunction from './FlowWindowFunction';
import SiWindows11 from '@mui/icons-material/Window';
import {
  RemoveWindowFunction,
  updateFlowPositions,
} from '../../../redux/Calculations/CalculationsActions';
import { CalculationFlowProps } from './CalculationFlowProps';
import { useReactFlow, useUpdateNodeInternals } from '@xyflow/react';

const DraggableCalculationFunctionItem = ({
  windowFn,
  flowUID,
  _flows,
  activeFlow,
  propKey,
  indexKey,
  flowDisplayName,
  resultantType,
  isFlowUsedInOtherFlowsAsSource,
  parentWidth,
  activeFlowName,
  aggregationType,
  deleteFlow,
  setActiveFlow,
  setFlowDisplayNames,
}: CalculationFlowProps) => {
  const calculationInfo = useSelector(
    (state: RootState) =>
      state.calculations.properties[propKey].currentCalculationSession?.calculationInfo,
  );
  const dispatch = useDispatch();
  const [filledProperly, setFilledProperly] = useState(false);
  const [showWinFn, setShowWinFn] = useState(false);
  const [showWnDialog, setShowWnDialog] = useState(false);
  const [anchorEl, setAnchorEl] = useState<any | null>(null);
  const [manuallyCorrected, setManuallyCorrected] = useState(false);
  const [initialOffsetLeft, setInitialOffsetLeft] = useState<number | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [flows, _] = useState(_flows);
  const [cardEditState, setCardEditState] = useState(false);
  const { screenToFlowPosition } = useReactFlow();
  const updateNodeInternals = useUpdateNodeInternals();

  const Boolean = createSvgIcon(
    <svg
      viewBox="0 0 76 76"
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      version="1.1"
      baseProfile="full"
      enable-background="new 0 0 76.00 76.00"
      xmlSpace="preserve"
      fill="#000000"
    >
      <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
      <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
      <g id="SVGRepo_iconCarrier">
        {' '}
        <path
          fill="#000000"
          fill-opacity="1"
          stroke-linejoin="round"
          d="M 36,23L 30,23L 30,40L 25,40L 25,23L 19,23L 19,19L 36,19L 36,23 Z M 57,40L 50,40L 50,45L 56,45L 56,49L 50,49L 50,57L 45,57L 45,36L 57,36L 57,40 Z M 44,19L 48.5,19L 32.5,57L 28,57L 44,19 Z "
        ></path>{' '}
      </g>
    </svg>,
    'Boolean',
  );
  useEffect(() => {
    setManuallyCorrected(false); // Allow re-check if parentWidth changes a lot
  }, [parentWidth]);

  useEffect(() => {
    const card = document.getElementById(flowUID);

    if (card && parentWidth) {
      const cardRect = card.getBoundingClientRect();

      if (initialOffsetLeft === null || card.offsetLeft !== 0) {
        setInitialOffsetLeft(card.offsetLeft);
      }
      const isOutOfBoundsLeftNow = initialOffsetLeft !== null && parentWidth < initialOffsetLeft;

      // setIsOutOfBounds(isOutOfBounds);

      if (!manuallyCorrected) {
        if (isOutOfBoundsLeftNow) {
          // setIsOutofBoundsLeft(true);
          setManuallyCorrected(true);
        } else {
          // setIsOutofBoundsLeft(false);
        }
      } else {
        const fullyVisible = card.offsetLeft >= 0 && cardRect.right <= parentWidth;
        if (fullyVisible) {
          setManuallyCorrected(false);
        }
      }
    }
  }, [flowUID, parentWidth, initialOffsetLeft, manuallyCorrected]);

  const [alert, setAlert] = useState<any>(null);

  const [{ isDragging: isDraggingFromHook }, drag, previewDrag] = useDrag({
    type: 'calculationSelected',
    item: {
      uid: flowUID,
      type: 'calculationSelected',
      name: flowDisplayName,
      DragFrom: Area.CALCULATION_CANVAS,
      fieldData: {
        dataType: resultantType,
        displayname: flowDisplayName,
        fieldname: flowDisplayName,
        isAggregated: aggregationType,
        // tableid: 'pos'
      },
      // preview: () => preview(flowDisplayName),
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    // this will update the flow position while drop cancel
    end: (item, monitor) => {
      const didDrop = monitor.didDrop();

      if (!didDrop) {
        const lastPointerPos = monitor.getClientOffset();
        if (!lastPointerPos) return;
        const position = screenToFlowPosition({
          x: lastPointerPos.x,
          y: lastPointerPos.y,
        });
        if (!position) return;
        dispatch(
          updateFlowPositions(propKey, item.uid, position.x.toString(), position.y.toString()),
        );
        updateNodeInternals(item.uid);
      }
    },
  });

  useEffect(() => {
    previewDrag(getEmptyImage(), { captureDraggingState: true });
  }, [previewDrag]);

  const itemTypeIcon = (type: string | undefined | null) => {
    switch (type) {
      case 'Integer':
      case 'integer':
        return (
          <img src="/Integer_Icon.svg" alt="Integer" style={{ height: '16px', width: '16px' }} />
        );

      case 'String':
      case 'string':
      case 'Text':
      case 'text':
        return <img src="/Text_Icon.svg" alt="Text" style={{ height: '14px', width: '15px' }} />;

      case 'Timestamp':
      case 'timestamp':
        return <AccessTime style={{ height: '15px', width: '15px' }} />;

      case 'Date':
      case 'date':
        return <CalendarToday style={{ height: '13px', width: '16px', padding: '1px' }} />;

      case 'Decimal':
      case 'decimal':
        return (
          <img src="/Decimal_Icon.svg" alt="Decimal" style={{ height: '20px', width: '17px' }} />
        );
      case 'boolean':
        return <Boolean style={{ height: '15px', width: '15px' }} />;
      default:
        return null;
    }
  };

  useEffect(() => {
    if (!calculationInfo) return;
    const flow = flows[flowUID];
    if (!flow) return;

    const properlyFilled = isFlowFilledProperly(calculationInfo, flowUID);
    setFilledProperly(properlyFilled);
  }, [calculationInfo, flowUID, flows]);
  const [displayName, setDisplayName] = useState(flowDisplayName);

  useEffect(() => {
    if (activeFlow === flowUID && isHovered) {
      setIsHovered(false);
    }
  }, [activeFlow, flowUID]);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      if (activeFlow === flowUID) {
        setActiveFlow(propKey, null);
      } else {
        setActiveFlow(propKey, flowUID);
      }
    },
    [propKey, flowUID, setActiveFlow],
  );

  const handleDoubleClick = useCallback(() => {
    if (cardEditState) return;

    setCardEditState(true);
  }, []);

  const addOrCreateWindowFunction = () => {
    setShowWnDialog(true);
    setShowWinFn(false);
  };
  const handleDialogClose = () => {
    setShowWnDialog(false);
    setAnchorEl(null);
    setShowWinFn(false);
  };

  const handleRename = (e: any) => {
    e.preventDefault();
    if (displayName.length >= 3 && displayName.length <= 30) {
      setFlowDisplayNames(propKey, flowUID, displayName);
      setCardEditState(false);
    } else {
      e.target.focus();
    }
  };
  const getWarningMessage = () => {
    if (!filledProperly) return 'This step is incomplete, either complete it or remove the step.';
    return '';
  };

  return (
    <>
      {alert && (
        <NotificationDialog
          severity={alert.severity}
          openAlert={alert}
          onCloseAlert={() => setAlert(null)}
          testMessage={alert.message}
        />
      )}

      <div
        id={flowUID}
        key={indexKey}
        ref={(el) => {
          drag(el);
        }}
        onDoubleClick={handleDoubleClick}
        style={{
          cursor: 'move',
          padding: '0.3rem',
          borderRadius: '8px',
          borderBottomRightRadius: activeFlow === flowUID ? '0px' : '8px',
          borderBottomLeftRadius: activeFlow === flowUID ? '0px' : '8px',
          border: cardEditState && displayName?.length < 3 ? '2px solid red' : '',
          color: ' #5d5c5c',
          textAlign: 'start',
          backgroundColor: activeFlow === flowUID ? '#f3f3f3ff' : 'white',
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: cardEditState ? '0px' : '10px',
          opacity: isDraggingFromHook ? 0.5 : 1,
          height: '35px',
          minWidth: `250px`,
          maxWidth: '300px',
        }}
      >
        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
          onClick={handleClick}
        >
          {cardEditState && (
            <Tooltip
              title={
                displayName?.length === 30
                  ? 'Maximum 30 characters allowed.'
                  : displayName?.length < 3
                    ? 'Minimum 3 characters required.'
                    : ''
              }
              arrow
              open={displayName?.length === 30 || displayName?.length < 3}
              placement="bottom"
              slotProps={{
                tooltip: {
                  sx: {
                    fontSize: '12px',
                  },
                },
              }}
            >
              <form
                style={{
                  display: 'flex',
                  width: '100%',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
                onSubmit={handleRename}
              >
                <input
                  autoFocus
                  type="text"
                  value={displayName}
                  onChange={(e) => {
                    setDisplayName(e.target.value);
                  }}
                  onBlur={handleRename}
                  style={{
                    width: '100%',
                    height: '1.5rem',
                    outline: 'none',
                    color: palette.primary.contrastText,
                    border: 'none',
                  }}
                  maxLength={30}
                  minLength={3}
                />
                <IconButton disabled={displayName.length < 3} onClick={handleRename}>
                  <DoneIcon />
                </IconButton>
              </form>
            </Tooltip>
          )}

          {!cardEditState && !isFlowUsedInOtherFlowsAsSource && (
            <button
              type="button"
              className="buttonCommon columnClose"
              title="Remove field"
              onClick={(e) => {
                e.stopPropagation();

                if (isFlowUsedInOtherFlowsAsSource) {
                  setAlert({
                    severity: 'warning',
                    message:
                      'This calculation is being used in another flow. Please remove the dependency before deleting.',
                  });
                  return;
                }

                deleteFlow(propKey, flowUID);

                // if (flowDisplayNames[flowUID] === 'Type Conversion') {
                //   dispatch(setTypeSelectionDropdown(propKey, ''));
                //   dispatch(setToggleButton(propKey, false));
                // }
              }}
            >
              <CloseRoundedIcon style={{ fontSize: '14px', margin: 'auto' }} />
            </button>
          )}
          {!cardEditState && isFlowUsedInOtherFlowsAsSource && (
            <Tooltip
              title="This calculation is being used in another flow. Please remove the dependency before deleting."
              slotProps={{
                tooltip: {
                  sx: {
                    fontSize: '12px',
                  },
                },
              }}
            >
              <LockIcon
                style={{
                  fontSize: '14px',
                  marginRight: '5px',
                  color: '#2BB9BB',
                }}
              />
            </Tooltip>
          )}
          {!cardEditState && (
            <Tooltip
              open={isHovered && activeFlow !== flowUID}
              title={'Click To Expand'}
              disableHoverListener={flowUID === activeFlow}
              placement="top"
              slotProps={{
                tooltip: {
                  sx: {
                    fontSize: '12px',
                  },
                },
              }}
            >
              <span
                style={{
                  color: activeFlow === flowUID ? '#2BB9BB' : palette.primary.contrastText,
                  fontSize: fontSize.small,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  flexGrow: 1,
                }}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                {' '}
                {flowDisplayName}{' '}
              </span>
            </Tooltip>
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {!cardEditState && (
            <>
              <span
                style={{
                  fontSize: fontSize.medium,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <Tooltip
                  // title={
                  //   filledProperly
                  //     ? 'One or more conditions are not filled properly'
                  //     : remainingSources !== 0 && flowName !== 'IfElse'
                  //       ? !doesSourceArrayContainFieldOrFlow
                  //         ? 'Please add at least one column from the table or flow to proceed.'
                  //         : `${totalSources} field${totalSources && totalSources > 1 ? 's' : ''} / flow${totalSources && totalSources > 1 ? 's' : ''} ${totalSources === 1 ? 'is' : 'are'} required minimum, ${remainingSources} ${remainingSources === 1 ? 'is' : 'are'} remaining`
                  //       : filledProperly && flowName === 'IfElse'
                  //         ? !doesSourceArrayContainFieldOrFlow
                  //           ? 'Please add at least one column from the table or flow to proceed.'
                  //           : 'One or more conditions are not filled properly'
                  //         : ''
                  // }
                  title={getWarningMessage()}
                  placement="top"
                  slotProps={{
                    tooltip: {
                      sx: {
                        fontSize: '12px',
                      },
                    },
                  }}
                >
                  <span style={{ display: 'flex', justifyItems: 'center', alignItems: 'center' }}>
                    {!filledProperly && <IoWarningOutline size={13} color="orange" />}
                  </span>
                </Tooltip>

                {windowFn && (
                  <button
                    type="button"
                    className="buttonCommon columnDown"
                    title="Window Function"
                    onClick={(e: any) => {
                      e.stopPropagation();
                      setAnchorEl(e.currentTarget);
                      addOrCreateWindowFunction();
                    }}
                  >
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        color: 'rgb(211, 211, 211)',
                      }}
                    >
                      <SiWindows11 />
                    </span>{' '}
                  </button>
                )}

                {itemTypeIcon(resultantType) && (
                  <Tooltip
                    title={
                      resultantType
                        ? `This step returns ${resultantType.charAt(0).toUpperCase() + resultantType.slice(1)} data type`
                        : null
                    }
                    placement="top"
                    slotProps={{
                      tooltip: {
                        sx: {
                          fontSize: '12px',
                        },
                      },
                    }}
                  >
                    <span
                      style={{
                        fontSize: fontSize.medium,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {itemTypeIcon(resultantType)}
                    </span>
                  </Tooltip>
                )}

                {aggregationType ? (
                  <Tooltip
                    title="This step returns Visual Level Aggregation"
                    placement="top"
                    slotProps={{
                      tooltip: {
                        sx: {
                          fontSize: '12px',
                        },
                      },
                    }}
                  >
                    <FunctionsIcon
                      sx={{
                        color: palette.primary.contrastText,
                        fontSize: '13px',
                      }}
                    />
                  </Tooltip>
                ) : (
                  <Tooltip
                      title="This step returns Row Level Calculation"
                    placement="top"
                    slotProps={{
                      tooltip: {
                        sx: {
                          fontSize: '12px',
                        },
                      },
                    }}
                  >
                    <MenuIcon
                      sx={{
                        color: palette.primary.contrastText,
                        fontSize: '15px',
                      }}
                    />
                  </Tooltip>
                )}

                <Menu
                  id="basic-menu"
                  anchorEl={anchorEl}
                  open={showWinFn}
                  onClose={() => {
                    setAnchorEl(null);
                    setShowWinFn(false);
                  }}
                  slotProps={{
                    list: {
                      'aria-labelledby': 'basic-button',
                    },
                  }}
                >
                  <MenuItem
                    onClick={() => {
                      if (activeFlow === flowUID) {
                        setActiveFlow(propKey, null);
                      } else {
                        setActiveFlow(propKey, flowUID);
                      }
                      setAnchorEl(null);
                      setShowWinFn(false);
                    }}
                  >
                    {activeFlow === flowUID ? 'Collapse' : 'Expand'}
                  </MenuItem>
                  <MenuItem
                    onClick={addOrCreateWindowFunction}
                    disabled={activeFlowName !== 'summarize'}
                  >
                    {windowFn ? 'Update' : 'Add'} Window Function
                  </MenuItem>
                  {windowFn && (
                    <MenuItem
                      onClick={(e) => {
                        dispatch(RemoveWindowFunction(propKey,flowUID));
                      }}
                    >
                      Remove Window Function
                    </MenuItem>
                  )}
                </Menu>
                <FlowWindowFunction
                  anchhorEl={anchorEl}
                  onClose={handleDialogClose}
                  open={showWnDialog}
                  propKey={propKey}
                  winfn={windowFn}
                  flowId={flowUID}
                />
              </span>

              <button
                type="button"
                className="buttonCommon columnDown"
                onClick={(e: any) => {
                  setAnchorEl(e.currentTarget);
                  setShowWinFn(!showWinFn);
                }}
                title="Click for Menu Options"
                style={{ padding: 0,marginLeft : "2px" }}
              >
                <MoreVertIcon style={{ fontSize: '15px', margin: 'auto' }} />
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
};
export default DraggableCalculationFunctionItem;
